import '@refinitiv-ui/elements/button';
import { CSSResult, LitElement, css, html } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import '../components/heimdall-error.js';
import '../components/heimdall-loader.js';
import '../components/heimdall-frame.js';
import '../components/user-id-prompt.js';
import { createIdpTokenBundle$, startRedirection } from '@heimdall/client';
import { VERSION } from '@lseg-ciam/auth';
import {
  Subject,
  map,
  of,
  switchMap,
  take,
  tap,
  type OperatorFunction,
} from 'rxjs';

console.log('[HEIMDALL] @lseg-ciam/auth', VERSION);
import {
  DEFAULT_IDP,
  defaultAudiencesByClientUid,
  knownLinkOrigins,
  workspaceFinalizeLoginHook,
} from '../workspace-config.js';
import { appConfig$, type AppConfig } from '../config.js';
import '../theme.js';

@customElement('heimdall-login')
export class HeimdallLogin extends LitElement {
  @state()
  _error: unknown = null;

  @state()
  _awaitingUserId = false;

  manualUserIdInput$ = new Subject<string>();

  firstUpdated() {
    appConfig$.pipe(this.withResolvedUserId()).subscribe({
      next: (config) => {
        this._awaitingUserId = false;
        startRedirection({
          ...config,
          clientUid: config.clientUid ?? 'workspace',
          knownLinkOrigins,
          onFinalizeLogin: workspaceFinalizeLoginHook,
          defaultAudiencesByClientUid,
        }).catch((error) => {
          this._error = error;
        });
      },
      error: (error) => {
        this._error = error;
      },
    });
  }

  withResolvedUserId(): OperatorFunction<AppConfig, AppConfig> {
    return (config$) =>
      config$.pipe(
        switchMap((config) => {
          if (config.idp || config.userId) {
            this._awaitingUserId = false;
            return of(config);
          }

          // Temporary: skip the user-id prompt. Remove to restore it.
          if (DEFAULT_IDP) {
            return of({ ...config, idp: DEFAULT_IDP });
          }

          return createIdpTokenBundle$(config.lane).pipe(
            take(1),
            map((bundle) =>
              bundle?.idpConfig?.userId ? bundle.idpConfig.userId : null
            ),
            tap((userId) => {
              this._awaitingUserId = !userId;
            }),
            switchMap((userId) =>
              userId ? of(userId) : this.manualUserIdInput$.pipe(take(1))
            ),
            map((userId) => ({ ...config, userId }))
          );
        })
      );
  }

  onUserIdSubmit(event: CustomEvent<string>) {
    this.manualUserIdInput$.next(event.detail);
  }

  render() {
    if (!this._error && !this._awaitingUserId) {
      return html`<heimdall-loader></heimdall-loader>`;
    }

    return html`<heimdall-frame> ${this.getFrameContent()} </heimdall-frame>`;
  }

  getFrameContent() {
    if (this._error) {
      return html`<heimdall-error .error=${this._error}></heimdall-error>`;
    }

    if (this._awaitingUserId) {
      return html`<user-id-prompt
        @user-id-submit=${this.onUserIdSubmit}
      ></user-id-prompt>`;
    }

    return html`<heimdall-loader></heimdall-loader>`;
  }

  static get styles(): CSSResult {
    return css`
      .button {
        margin: 0 10px 10px 0;
      }
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'heimdall-login': HeimdallLogin;
  }
}
